/**
 * Advanced Simulation Dashboard
 *
 * Main entry point for the Advanced Simulation System.
 * Provides tabs for single runs, batch testing, history, and clone studio.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Alert,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  BatchPrediction as BatchIcon,
  History as HistoryIcon,
  ContentCopy as CloneIcon,
  AutoFixHigh as RLIcon,
  Science as ABTestIcon,
  QueueMusic as AudioIcon,
  SmartToy as WizardIcon,
  Send as SendIcon
} from '@mui/icons-material';
import SingleRunForm from './SingleRunForm';
import BatchConfigForm from './BatchConfigForm';
import AdvancedSimulationViewer from './AdvancedSimulationViewer';
import SimulationCloneEditor from './SimulationCloneEditor';
import RLLoopDashboard from './RLLoopDashboard';
import ABTestDashboard from './ABTestDashboard';
import SimulationAudioExportQueue from './SimulationAudioExportQueue';
import { startPersonaWizard, sendPersonaWizardMessage, applyPersonaWizardPersonas } from '../../../services/advancedSimulationApi';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simulation-tabpanel-${index}`}
      aria-labelledby={`simulation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simulation-tab-${index}`,
    'aria-controls': `simulation-tabpanel-${index}`,
  };
}

function PersonaWizard({ token }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [generatedPersonas, setGeneratedPersonas] = useState(null);
  const [applyingPersonas, setApplyingPersonas] = useState(false);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startWizard = async (instruction = '') => {
    setLoading(true);
    setError(null);
    try {
      const data = await startPersonaWizard(instruction, token);
      setSessionId(data.sessionId);
      setMessages([{ role: 'wizard', content: data.text }]);
      setGeneratedPersonas(null);
      setSessionCompleted(false);
    } catch (err) {
      setError(err.error || err.message || 'Failed to start wizard');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || !sessionId || loading) return;
    const userMsg = inputText.trim();
    setInputText('');
    setMessages(prev => [...prev, { role: 'admin', content: userMsg }]);
    setLoading(true);
    setError(null);
    try {
      const data = await sendPersonaWizardMessage(sessionId, userMsg, token);
      setMessages(prev => [...prev, { role: 'wizard', content: data.text }]);
      if (data.hasConfig && data.personas) {
        setGeneratedPersonas(data.personas);
      }
    } catch (err) {
      setError(err.error || err.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const applyAllPersonas = async () => {
    if (!sessionId || applyingPersonas) return;
    setApplyingPersonas(true);
    setError(null);
    try {
      const data = await applyPersonaWizardPersonas(sessionId, token);
      const hasFailures = data.failed && data.failed.length > 0;
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Successfully created ${data.created.length} persona(s): ${data.created.map(p => p.name).join(', ')}${hasFailures ? `. Failed: ${data.failed.map(f => `${f.key} (${f.error})`).join(', ')}` : ''}`
      }]);
      setGeneratedPersonas(null);
      if (data.status === 'completed') {
        setSessionCompleted(true);
      }
    } catch (err) {
      setError(err.error || err.message || 'Failed to apply personas');
    } finally {
      setApplyingPersonas(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetWizard = () => {
    setSessionId(null);
    setMessages([]);
    setGeneratedPersonas(null);
    setSessionCompleted(false);
    setError(null);
    setInputText('');
  };

  // Not started yet
  if (!sessionId) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Client Persona Wizard</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use AI to design realistic 4-layer client personas through a guided conversation.
          Describe the kind of client(s) you need, and the wizard will generate complete persona configurations.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button
          variant="contained"
          onClick={() => startWizard()}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <WizardIcon />}
        >
          {loading ? 'Starting...' : 'Start Wizard'}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Client Persona Wizard</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`Session: ${sessionId.slice(-8)}`} size="small" variant="outlined" />
          {sessionCompleted && (
            <Button size="small" variant="outlined" onClick={resetWizard} startIcon={<WizardIcon />}>
              New Session
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Chat messages */}
      <Paper variant="outlined" sx={{ height: 400, overflowY: 'auto', p: 2, mb: 2, bgcolor: 'grey.50' }}>
        {messages.map((msg, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'admin' ? 'flex-end' : 'flex-start',
              mb: 1.5
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                maxWidth: '80%',
                bgcolor: msg.role === 'admin' ? 'primary.main' : msg.role === 'system' ? 'success.light' : 'white',
                color: msg.role === 'admin' ? 'white' : 'text.primary',
              }}
            >
              <Typography variant="caption" color={msg.role === 'admin' ? 'inherit' : 'text.secondary'} sx={{ fontWeight: 600 }}>
                {msg.role === 'admin' ? 'You' : msg.role === 'system' ? 'System' : 'Wizard'}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {msg.content}
              </Typography>
            </Paper>
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
            <Paper elevation={1} sx={{ p: 1.5, bgcolor: 'white' }}>
              <CircularProgress size={20} />
            </Paper>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Paper>

      {/* Input area */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={sessionCompleted ? 'Session completed — start a new session to continue' : 'Describe the personas you need...'}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || sessionCompleted}
          multiline
          maxRows={3}
        />
        <IconButton color="primary" onClick={sendMessage} disabled={loading || sessionCompleted || !inputText.trim()}>
          <SendIcon />
        </IconButton>
      </Box>

      {/* Generated personas preview */}
      {generatedPersonas && generatedPersonas.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Generated Personas ({generatedPersonas.length})
            </Typography>
            <Button
              variant="contained"
              color="success"
              onClick={applyAllPersonas}
              disabled={applyingPersonas}
              startIcon={applyingPersonas ? <CircularProgress size={16} /> : null}
            >
              {applyingPersonas ? 'Applying...' : 'Apply All'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {generatedPersonas.map((p, idx) => (
              <Card key={idx} variant="outlined" sx={{ minWidth: 240, flex: '1 1 280px' }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.key}</Typography>
                  {p.metadata?.conditions && (
                    <Box sx={{ mt: 0.5 }}>
                      {p.metadata.conditions.map((c, i) => (
                        <Chip key={i} label={c} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  )}
                  {p.metadata?.severity && (
                    <Chip label={p.metadata.severity} size="small" color="warning" sx={{ mt: 0.5 }} />
                  )}
                  {p.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                      {p.description.length > 120 ? p.description.substring(0, 120) + '...' : p.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function AdvancedSimulationDashboard({ token, isResearcher = false }) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRunForClone, setSelectedRunForClone] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSelectRunForClone = (runId) => {
    setSelectedRunForClone(runId);
    setActiveTab(3); // Switch to clone studio tab
  };

  const handleRunCompleted = (runId) => {
    // Could switch to history or show notification
    console.log('Run completed:', runId);
  };

  const handleBatchCompleted = (batchId) => {
    // Could switch to history or show notification
    console.log('Batch completed:', batchId);
  };

return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Advanced Simulation System
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Run batch simulations, compare prompt variations, and iterate on therapist prompts with rich analytics.
      </Typography>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="simulation tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<RunIcon />}
              iconPosition="start"
              label="Single Run"
              {...a11yProps(0)}
            />
            <Tab
              icon={<BatchIcon />}
              iconPosition="start"
              label="Batch Testing"
              {...a11yProps(1)}
            />
            <Tab
              icon={<HistoryIcon />}
              iconPosition="start"
              label="History"
              {...a11yProps(2)}
            />
            <Tab
              icon={<CloneIcon />}
              iconPosition="start"
              label="Clone Studio"
              {...a11yProps(3)}
            />
            <Tab
              icon={<RLIcon />}
              iconPosition="start"
              label="RL Loop"
              {...a11yProps(4)}
            />
            <Tab
              icon={<ABTestIcon />}
              iconPosition="start"
              label="A/B Testing"
              {...a11yProps(5)}
            />
            {!isResearcher && (
              <Tab
                icon={<AudioIcon />}
                iconPosition="start"
                label="Audio Exports"
                {...a11yProps(6)}
              />
            )}
            {!isResearcher && (
              <Tab
                icon={<WizardIcon />}
                iconPosition="start"
                label="Persona Wizard"
                {...a11yProps(7)}
              />
            )}
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <SingleRunForm
            token={token}
            onRunCompleted={handleRunCompleted}
            isResearcher={isResearcher}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <BatchConfigForm
            token={token}
            onBatchCompleted={handleBatchCompleted}
            isResearcher={isResearcher}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <AdvancedSimulationViewer
            token={token}
            isResearcher={isResearcher}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <SimulationCloneEditor
            token={token}
            initialRunId={selectedRunForClone}
            onCloneCompleted={(newRunId) => {
              setSelectedRunForClone(null);
              handleRunCompleted(newRunId);
            }}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <RLLoopDashboard token={token} isResearcher={isResearcher} />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <ABTestDashboard token={token} isResearcher={isResearcher} />
        </TabPanel>

        {!isResearcher && (
          <TabPanel value={activeTab} index={6}>
            <SimulationAudioExportQueue token={token} />
          </TabPanel>
        )}

        {!isResearcher && (
          <TabPanel value={activeTab} index={7}>
            <PersonaWizard token={token} />
          </TabPanel>
        )}
      </Paper>
    </Container>
  );
}

export default AdvancedSimulationDashboard;
